import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { authClient } from "@/lib/auth-client";
import { Loader2 } from "lucide-react";
import { Link } from "react-router";

const loginSchema = z.object({
  password: z.string().min(1, "Le mot de passe est requis"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface InvitationLoginFormProps {
  email: string;
  emailRedacted: string;
  invitationId: string;
  onSuccess: (organizationId: string) => void;
  onError: (message: string) => void;
}

export function InvitationLoginForm({
  email,
  emailRedacted,
  invitationId,
  onSuccess,
  onError,
}: InvitationLoginFormProps) {
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      password: "",
    },
  });

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);

    try {
      // 1. Login
      const loginResult = await authClient.signIn.email({
        email,
        password: data.password,
      });

      if (loginResult.error) {
        if (loginResult.error.code === "INVALID_EMAIL_OR_PASSWORD") {
          form.setError("password", {
            type: "manual",
            message: "Mot de passe incorrect",
          });
        } else {
          onError(loginResult.error.message || "Erreur lors de la connexion");
        }
        setIsLoading(false);
        return;
      }

      // 2. Accept invitation
      const acceptResult = await authClient.organization.acceptInvitation({
        invitationId,
      });

      if (acceptResult.error) {
        onError(acceptResult.error.message || "Erreur lors de l'acceptation de l'invitation");
        setIsLoading(false);
        return;
      }

      // 3. Get the organization ID from the invitation to set as active
      // We need to fetch invitation info to get organizationId
      const invitationsResult = await authClient.organization.listInvitations({});
      const invitation = invitationsResult.data?.find((inv) => inv.id === invitationId);

      if (invitation?.organizationId) {
        await authClient.organization.setActive({
          organizationId: invitation.organizationId,
        });
        onSuccess(invitation.organizationId);
      } else {
        // Fallback: just navigate, the app will handle org selection
        onSuccess("");
      }
    } catch (error) {
      onError((error as Error).message || "Erreur inattendue");
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          {/* Email display (redacted, non-editable) */}
          <div className="grid gap-2">
            <label className="text-sm font-medium">Email</label>
            <Input value={emailRedacted} disabled className="bg-muted" />
          </div>

          {/* Password field */}
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="grid gap-2">
                <div className="flex items-center justify-between">
                  <FormLabel>Mot de passe</FormLabel>
                  <Link
                    to={`/forget-password?email=${encodeURIComponent(email)}`}
                    className="text-sm text-muted-foreground underline-offset-4 hover:underline"
                    tabIndex={-1}
                  >
                    Mot de passe oublié?
                  </Link>
                </div>
                <FormControl>
                  <Input type="password" autoFocus {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Rejoindre le club
        </Button>
      </form>
    </Form>
  );
}
